import React, { useState } from 'react';
import { Utensils, ShieldCheck, Activity, ChevronLeft, ChevronRight, Star, ArrowRight, Lock } from 'lucide-react';
import { wallOfWinsRow1, wallOfWinsRow2, videoTestimonials, BUNNY_LIBRARY_ID } from './winsData';
import BunnyVideo from './BunnyVideo';
import { submitLead } from './submitLead';

const FemalePricingPage: React.FC = () => {
  const included = [
    "/icons3d/card-no-gym.jpg",
    "/icons3d/card-nutrition.jpg",
    "/icons3d/card-support.jpg",
    "/icons3d/card-lifetime.jpg",
  ];

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  // Video ending: jump to the apply form on this page / slide to the next story
  const goToApplyForm = () => document.getElementById('female-apply-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const showNextStory = (i: number) =>
    document.getElementById(`pstory-${i + 1}`)?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });

  const scrollToPricing = () => document.getElementById('female-pricing-box')?.scrollIntoView({ behavior: 'smooth' });

  const submitApplication = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(false);
    const ok = await submitLead({ name, phone });
    if (ok) setSubmitted(true);
    else setSubmitError(true);
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-black text-white font-sans">

      <header className="bg-black border-b border-zinc-800 py-4 flex justify-center">
        <img
          src="https://firebasestorage.googleapis.com/v0/b/accountability-566c2.firebasestorage.app/o/088529ce-52f4-4ef7-a65f-0923d5901386.png?alt=media"
          alt="Z.A Training Logo"
          className="h-12 md:h-16 object-contain"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      </header>

      {/* HERO */}
      <section className="w-full max-w-5xl mx-auto px-4 pt-12 pb-16 text-center">
        <div className="flex items-center justify-center gap-3 mb-8">
          <span className="hidden sm:block h-px w-10 bg-zinc-700"></span>
          <p className="flex items-center gap-2 text-zinc-400 text-[11px] md:text-xs font-semibold uppercase tracking-[0.2em]">
            <span className="text-amber-400 text-sm">★★★★★</span>
            Trusted by 1000+ South Asians
          </p>
          <span className="hidden sm:block h-px w-10 bg-zinc-700"></span>
        </div>
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold leading-tight text-white mb-6 tracking-tight">
          Predictable weight loss for <br className="hidden md:block" />
          <span className="text-red-600">South Asian Women</span>
        </h1>
        <p className="text-xl md:text-3xl text-zinc-200 max-w-3xl mx-auto leading-relaxed mb-8 font-bold">
          Costs less than{' '}
          <span className="text-red-500 underline decoration-wavy decoration-red-700 underline-offset-4">Mounjaro</span>
          . Works longer than{' '}
          <span className="text-red-500 underline decoration-wavy decoration-red-700 underline-offset-4">Mounjaro</span>
          .
        </p>

        <button
          onClick={scrollToPricing}
          className="inline-flex items-center gap-2 bg-zinc-900 border border-zinc-700 hover:border-red-600 text-zinc-200 text-xs md:text-sm font-bold px-5 py-2.5 rounded-full mb-8 transition-colors shadow-sm"
        >
          💉 Already on Mounjaro or Ozempic? We work with that too →
        </button>

        <div className="flex flex-wrap justify-center gap-10 md:gap-20 py-6 mb-4">
          <div className="text-center">
            <h4 className="text-4xl md:text-5xl font-black mb-1 text-white">500+</h4>
            <p className="text-[11px] md:text-xs font-bold text-zinc-500 uppercase tracking-widest">Women Transformed</p>
          </div>
        </div>

        <div className="w-full max-w-5xl mx-auto mb-4 px-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: <Utensils className="w-7 h-7" />, title: "Your Nutrition, Mapped To Your Life" },
              { icon: <ShieldCheck className="w-7 h-7" />, title: "Daily Support, Not Just a PDF" },
              { icon: <Activity className="w-7 h-7" />, title: "Training That Fits Your Reality" }
            ].map((p, i) => (
              <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-3xl p-7 text-left shadow-sm hover:border-red-700 transition-all flex items-center gap-4">
                <div className="inline-flex p-3 bg-red-700/15 text-red-500 rounded-2xl shrink-0">{p.icon}</div>
                <h3 className="text-lg md:text-xl font-bold text-white">{p.title}</h3>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 flex justify-center px-2">
          <div className="inline-flex items-center gap-4 bg-zinc-900 border border-zinc-800 rounded-full pl-3 pr-6 py-2.5">
            <div className="flex -space-x-3">
              {['avatar-1', 'avatar-2', 'avatar-3', 'avatar-4', 'avatar-5', 'avatar-6'].map((a, i) => (
                <img
                  key={i}
                  src={`/avatars/${a}.jpg`}
                  alt="Client"
                  className="w-9 h-9 rounded-full object-cover border-2 border-zinc-900"
                />
              ))}
            </div>
            <p className="text-zinc-300 text-sm md:text-base font-semibold">Trusted by 1000+ South Asians</p>
          </div>
        </div>
      </section>

      {/* MEET YOUR COACH — reused from the main female landing page */}
      <section className="w-full bg-[#111827] py-24 px-4 text-white">
        <div className="max-w-5xl mx-auto text-center space-y-12">

          <div className="space-y-6">
            <p className="text-red-500 font-black uppercase tracking-widest text-sm md:text-base">Meet Your Coach</p>
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight">
              Hi, I'm Zayb. <br className="hidden md:block" />
              Real-World Experience. <br className="hidden md:block" />
              No Fad Diets.
            </h2>
            <p className="text-slate-400 text-lg md:text-xl max-w-3xl mx-auto font-medium leading-relaxed">
              I'm not teaching ideas from a textbook. Everything you'll discover about building your body, scaling your energy, and future-proofing your health comes from hard-won, real-world experience helping hundreds of South Asian women transform.
            </p>
          </div>

          <div className="max-w-md mx-auto pt-6">
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-slate-800 aspect-[4/5] relative group">
              <img
                src="/coach-photo.jpg"
                alt="Zayb — Founder of Z.A Training"
                className="absolute inset-0 w-full h-full object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-700 ease-in-out"
              />
            </div>
          </div>

        </div>
      </section>

      {/* VIDEO TESTIMONIALS */}
      <div className="w-full mt-4 mb-20">
        <div className="text-center mb-10 px-4">
          <h2 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight">Don't take my word for it.</h2>
          <p className="text-zinc-400 text-lg mt-4">Hear it from the women who've been through it.</p>
          <p className="text-red-500 text-sm md:text-base mt-3 font-bold uppercase tracking-wide flex items-center justify-center gap-2">
            <ChevronLeft className="w-4 h-4" /> Swipe across &amp; tap a video to play <ChevronRight className="w-4 h-4" />
          </p>
        </div>

        <div
          className="flex gap-5 px-5 overflow-x-auto hide-scrollbar snap-x snap-mandatory scroll-smooth"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {videoTestimonials.map((test, i) => (
            <div
              key={i}
              id={`pstory-${i}`}
              className="w-64 sm:w-72 shrink-0 snap-start bg-zinc-900 rounded-3xl shadow-md border border-zinc-800 overflow-hidden flex flex-col text-left"
            >
              <div className="aspect-square bg-zinc-950 relative overflow-hidden">
                <BunnyVideo
                  libraryId={BUNNY_LIBRARY_ID}
                  videoId={test.bunnyId}
                  title={`Testimonial ${i + 1}`}
                  onCta={goToApplyForm}
                  onNext={i < videoTestimonials.length - 1 ? () => showNextStory(i) : undefined}
                  compact
                />
              </div>
              <div className="p-4 text-center bg-zinc-900">
                <div className="flex justify-center gap-0.5 text-amber-400 mb-2">
                  <Star className="h-3.5 w-3.5 fill-current" /><Star className="h-3.5 w-3.5 fill-current" /><Star className="h-3.5 w-3.5 fill-current" /><Star className="h-3.5 w-3.5 fill-current" /><Star className="h-3.5 w-3.5 fill-current" />
                </div>
                <p className="font-semibold text-zinc-300 text-xs leading-relaxed italic line-clamp-3">"{test.quote}"</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* WALL OF WINS */}
      <div className="w-full mb-16">
        <div className="text-center mb-8 px-4">
          <h2 className="text-2xl md:text-4xl font-black text-white uppercase tracking-tighter">
            Real Women. Real Results.
          </h2>
          <p className="text-zinc-400 mt-2">Unfiltered screenshots from real clients.</p>
        </div>

        <div className="marquee-row relative overflow-hidden mb-4 [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
          <div className="animate-marquee-left gap-5 pl-5">
            {[...wallOfWinsRow1, ...wallOfWinsRow1].map((win, idx) => (
              <div key={idx} className="w-64 shrink-0 bg-zinc-900 p-3 rounded-2xl shadow-md border border-zinc-800">
                <p className="text-xs font-semibold text-zinc-300 mb-2 px-1 line-clamp-2 h-8">{win.caption}</p>
                <div className="rounded-xl overflow-hidden border border-zinc-800 bg-zinc-950 aspect-[4/5]">
                  <img src={win.image} alt={win.caption} loading="lazy" className="w-full h-full object-contain" />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="marquee-row relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
          <div className="animate-marquee-right gap-5 pl-5">
            {[...wallOfWinsRow2, ...wallOfWinsRow2].map((win, idx) => (
              <div key={idx} className="w-64 shrink-0 bg-zinc-900 p-3 rounded-2xl shadow-md border border-zinc-800">
                <p className="text-xs font-semibold text-zinc-300 mb-2 px-1 line-clamp-2 h-8">{win.caption}</p>
                <div className="rounded-xl overflow-hidden border border-zinc-800 bg-zinc-950 aspect-[4/5]">
                  <img src={win.image} alt={win.caption} loading="lazy" className="w-full h-full object-contain" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* POLAROID TESTIMONIALS */}
      <div className="flex flex-col sm:flex-row justify-center items-center gap-8 mb-20 px-4">
        <div className="bg-white p-3 pb-16 rounded-sm shadow-2xl max-w-[280px] -rotate-2 relative">
          <img src="/polaroid-female-1.jpg" alt="Client testimonial" className="w-full rounded-sm" />
          <p className="text-zinc-800 text-sm font-medium italic text-center px-2 mt-4 leading-snug" style={{ fontFamily: 'Georgia, serif' }}>
            "This ended up costing me only £50 a month due to the amount of savings these guys got me, along with all the weight loss. So I actually saved more money working with them. And I lost the weight."
          </p>
        </div>
        <div className="bg-white p-3 pb-16 rounded-sm shadow-2xl max-w-[280px] rotate-2 relative">
          <img src="/polaroid-female-2.jpg" alt="Client testimonial" className="w-full rounded-sm" />
          <p className="text-zinc-800 text-sm font-medium italic text-center px-2 mt-4 leading-snug" style={{ fontFamily: 'Georgia, serif' }}>
            "I'm genuinely kicking myself for not joining sooner. The biggest regret I've got."
          </p>
        </div>
      </div>

      {/* WHAT'S INCLUDED — James Smith style value-stack breakdown */}
      <section className="w-full max-w-6xl mx-auto px-4 mb-16">
        <div className="text-center mb-4">
          <h2 className="text-3xl md:text-5xl font-black text-white leading-tight mb-3">
            Coaching Gives You Everything<br className="hidden md:block" /> You Need To Transform Your Body
          </h2>
          <p className="text-zinc-400 text-sm md:text-base">Based on the exact system I've used to help 500+ South Asian women lose weight for good.</p>
        </div>

        <div className="flex gap-5 px-1 py-10 overflow-x-auto hide-scrollbar snap-x snap-mandatory scroll-smooth">
          {included.map((src, i) => (
            <div key={i} className="w-60 shrink-0 snap-start rounded-2xl overflow-hidden">
              <img src={src} alt="" className="w-full h-auto object-contain" />
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <div id="female-pricing-box" className="max-w-2xl mx-auto px-4 pb-16">

        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5 text-center mb-14">
          <p className="text-zinc-300 text-sm md:text-base font-semibold">
            Mounjaro costs <span className="text-white font-black">£150–£350 a month</span> on its own.
          </p>
          <p className="text-zinc-500 text-sm mt-1">This costs less — and it works even after you stop.</p>
        </div>

        <div className="relative bg-zinc-900 border border-zinc-800 rounded-3xl px-8 md:px-12 py-10 mb-14 mx-2">
          <span className="absolute -top-6 left-6 text-7xl md:text-8xl font-black text-red-700/25 leading-none select-none pointer-events-none" style={{ fontFamily: 'Georgia, serif' }}>"</span>
          <p className="relative text-zinc-200 text-lg md:text-xl font-bold leading-relaxed max-w-lg mx-auto text-center">
            This isn't a "try it and see" service. With 12+ years of guaranteed results behind me, we only take on women who are serious about transforming.
            <span className="block text-red-500 mt-3">You don't need to be experienced. You just need to have a dream and a desire.</span>
          </p>
          <span className="absolute -bottom-14 right-6 text-7xl md:text-8xl font-black text-red-700/25 leading-none select-none pointer-events-none rotate-180" style={{ fontFamily: 'Georgia, serif' }}>"</span>
        </div>

        <div className="text-center mb-14">
          <h2 className="text-2xl md:text-3xl font-extrabold uppercase mb-4 leading-tight text-white">
            SAME MONEY.<br /><span className="text-red-500">COMPLETELY DIFFERENT RETURN.</span>
          </h2>
          <p className="text-zinc-400 text-sm">
            Daily coaching. A plan built around your life. Real, lasting results.
          </p>
        </div>

        <div className="bg-yellow-400 rounded-3xl p-8 md:p-10 text-center shadow-[0_20px_50px_-10px_rgba(250,204,21,0.3)] border-4 border-black mb-14">
          <p className="text-black text-xs font-black uppercase tracking-[0.3em] mb-3">The Guarantee</p>
          <h2 className="text-2xl md:text-3xl font-black text-black uppercase leading-tight mb-4">
            EXPERIENCE LIFE WITH US<br />FOR 14 DAYS.
          </h2>
          <p className="text-black text-base md:text-lg font-bold leading-snug max-w-md mx-auto">
            If you don't think it's for you, I'll refund you personally — and buy you a burger and fries for wasting your time.
          </p>
        </div>

        <div id="female-apply-form" className="bg-zinc-900 border border-zinc-800 rounded-3xl p-8 md:p-10 text-center">
          {submitted ? (
            <>
              <h2 className="text-2xl md:text-3xl font-black text-white mb-2">You're in!</h2>
              <p className="text-zinc-400 font-bold text-lg">Keep an eye on your WhatsApp, {name}. I'll be reaching out shortly.</p>
            </>
          ) : (
            <>
              <h2 className="text-2xl md:text-3xl font-black text-white mb-2">Apply Now</h2>
              <p className="text-zinc-400 font-bold mb-8">Leave your details and I will personally reach out.</p>
              <form onSubmit={submitApplication} className="space-y-4 max-w-sm mx-auto">
                <input
                  type="text" required placeholder="Your Full Name" value={name} onChange={(e) => setName(e.target.value)}
                  className="w-full px-5 py-4 rounded-xl border-2 border-zinc-700 focus:ring-0 focus:border-red-500 outline-none transition-all text-lg font-medium bg-zinc-950 text-white"
                />
                <input
                  type="tel" required placeholder="WhatsApp Number" value={phone} onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-5 py-4 rounded-xl border-2 border-zinc-700 focus:ring-0 focus:border-red-500 outline-none transition-all text-lg font-medium bg-zinc-950 text-white"
                />
                {submitError && <p className="text-red-400 text-sm font-bold">Sorry, something went wrong sending your details. Please try again in a moment.</p>}
                <button type="submit" disabled={isSubmitting} className="w-full mt-2 bg-white hover:bg-zinc-200 text-black font-black text-xl py-5 px-4 rounded-xl transition-all flex items-center justify-center gap-3 transform hover:-translate-y-1 uppercase tracking-wider disabled:opacity-50">
                  {isSubmitting ? 'Sending...' : (
                    <>
                      <Lock className="h-5 w-5 shrink-0" strokeWidth={3} />
                      Apply Now
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>

      </div>

      <style dangerouslySetInnerHTML={{__html: `
        @keyframes marquee-left { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        @keyframes marquee-right { 0% { transform: translateX(-50%); } 100% { transform: translateX(0); } }
        .animate-marquee-left { display: flex; width: fit-content; animation: marquee-left 55s linear infinite; }
        .animate-marquee-right { display: flex; width: fit-content; animation: marquee-right 65s linear infinite; }
        .marquee-row:hover .animate-marquee-left, .marquee-row:hover .animate-marquee-right { animation-play-state: paused; }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}} />

    </div>
  );
};

export default FemalePricingPage;
